/**
 * Persistent "path to your first leak report" card on the Drills home.
 * Keeps the AHA goal in front of a new (free) user between the milestone
 * nudge pops. Auto-hides the moment they hit the 25-signal gate (once the
 * report unlocks there's nothing to chase). Reads the same
 * `["billing-status"]` query everything else uses.
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { getBillingStatus } from "@/features/billing/lib/billing";
import { ChevronRight, ICON_STROKE, Target } from "@/shared/components/icons";

export function FirstReportProgressCard() {
  const { data: status } = useQuery({
    queryKey: ["billing-status"],
    queryFn: getBillingStatus,
    staleTime: 60_000,
  });

  if (!status) return null;
  // Free users still building toward their first report.
  if (status.subscriptionStatus !== "free") return null;
  if (status.signalCount >= status.signalsForFirstReport) return null;

  const pct =
    status.signalsForFirstReport > 0
      ? Math.min(100, Math.round((status.signalCount / status.signalsForFirstReport) * 100))
      : 0;
  const remaining = Math.max(0, status.signalsForFirstReport - status.signalCount);

  return (
    <section className="rounded-2xl border border-accent/40 bg-accent/[0.07] p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-9 h-9 shrink-0 rounded-xl bg-accent/20 text-accent flex items-center justify-center border border-accent/20">
            <Target size={18} strokeWidth={ICON_STROKE} />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.15em] text-accent font-semibold">
              Your first leak report
            </div>
            <div className="text-sm font-medium text-text">
              Keep drilling to unlock your first read on your game
            </div>
          </div>
        </div>
        <span className="text-sm font-semibold text-accent tabular-nums shrink-0">
          {status.signalCount} / {status.signalsForFirstReport}
        </span>
      </div>

      <div className="h-2 rounded-full bg-bg/60 overflow-hidden">
        <div
          className="h-full rounded-full bg-accent transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="flex items-center justify-between text-[11px] text-text-muted">
        <span>
          {remaining} more coach {remaining === 1 ? "read" : "reads"} to go
        </span>
        <Link
          to="/skills"
          className="inline-flex items-center gap-0.5 text-accent hover:text-accent/80 font-medium"
        >
          What's a leak report?
          <ChevronRight size={12} strokeWidth={ICON_STROKE} />
        </Link>
      </div>
    </section>
  );
}
